import type { FeatureFlagKey } from '@workspace/api-backend'

/**
 * Human-readable copy for each registered feature flag. Lives in code (not the
 * DB) — adding a new flag in `apps/backend/src/domain/feature-flag.models.ts`
 * requires a corresponding entry here. TypeScript's exhaustive Record ensures
 * the build fails if the two registries drift.
 */
/** Whose allowlist a flag uses while it is off: startups (the default) or investors. */
export type FeatureFlagAudience = 'startups' | 'investors'

export const FEATURE_FLAG_META: Record<
  FeatureFlagKey,
  { title: string; description: string; audience?: FeatureFlagAudience }
> = {
  FREE_PITCH_SESSION: {
    title: 'Free AI Pitch Session',
    description:
      'When the flag is on, every startup gets its first AI pitch session free (no credits needed) and the founder UI highlights the limited-time offer. When the flag is off, only allowlisted startups get it.',
  },
  OUTREACH: {
    title: 'Founder Outreach',
    description:
      'Automated LinkedIn outreach via HeyReach. When the flag is off, only allowlisted startups see the activation flow — everyone else sees a "coming soon" landing page.',
  },
  PORTFOLIO_OUTREACH: {
    title: 'Portfolio Founder Outreach',
    description:
      'Enables the portfolio outreach channel — contacting founders of VC portfolio companies to request warm intros to shared investors. Requires OUTREACH to already be active.',
  },
  AUTOMATED_EMAILS: {
    title: 'Automated Emails',
    description:
      'When on, due automated lifecycle emails are sent automatically. When off, they are still recorded as pending under Admin → Emails so you can review and send them manually before going live.',
  },
  OUTREACH_COPY_ARMS: {
    title: 'Outreach Copy A/B',
    description:
      'When on, each lead is assigned one copy variant — control or a generated arm — and every message it receives comes from that arm. When off (the default) every lead gets the control, which is exactly the behaviour before the experiment existed. A founder edit always wins for the field they edited, whatever this is set to.',
  },
  INTEREST_GATE: {
    title: 'Share Link Interest Gate',
    description:
      'Asks investors on a share link to declare "Interested" or "Not a fit" after the team section, revealing the evaluation only once they say they are interested. This is a hard block on a page founders send to investors — turn it off to remove it immediately, without a deploy. Verdicts already recorded are kept.',
  },
  PROFILE_CHAT: {
    title: 'Ask the Profile',
    description:
      'Global kill switch for the chat investors can use to ask questions about a startup on its share link. A startup only gets the chat when this flag is on AND the founder has enabled it, so turning the flag on never switches it on for anyone who did not ask — but turning it off removes it from everyone, everywhere, with no deploy. The flag alone also warms the deck slide cache for pilot startups.',
  },
  GOOGLE_SIGN_IN: {
    title: 'Google Sign-in',
    description:
      'Shows "Continue with Google" on the /app login. Global only — the startup allowlist below has no effect, since no startup exists before someone is logged in. Off hides the button and immediately blocks new Google sign-ins, including a callback already in progress; Google users can still log in with an email code, and existing sessions are untouched. Only Gmail and Google Workspace accounts are accepted; other Google identities are rejected regardless of this flag.',
  },
  INVESTOR_MCP: {
    title: 'Investor MCP',
    audience: 'investors',
    description:
      'The investor AI connection (the /mcp server, the sign-in it needs, and the assistant pages in the investor app). On: open to every investor. Off: open only to the investors on the allowlist below, and to nobody else — assistants of anyone else get a 503 or a 403 and cannot start or finish connecting one. Connections that already exist stay on file and can still be revoked.',
  },
  INVESTOR_WATCHDOG_EMAILS: {
    title: 'Investor watchdog emails',
    audience: 'investors',
    description:
      'Sends each investor with the assistant at most one email a week, and only when there are new matching startups. Off (the default): matches are computed and shown in the app and nothing is emailed. Also needs Automated Emails on. Turn on after the preview under Admin has been checked.',
  },
  INVESTOR_INVITE_EMAILS: {
    title: 'Investor invite emails',
    audience: 'investors',
    description:
      'Emails the investor when a founder they invited joins and posts their deck ("joined through your invite"). Off (the default): the follow still starts and the bell notification still appears; only the email is skipped. Also needs Automated Emails on.',
  },
  EMAIL_UPDATES: {
    title: 'Email updates',
    description:
      'Global kill switch for startup updates received by email (the Resend inbound webhook and the email-updates endpoints). Global only — the startup allowlist below has no effect. Off: the webhook and the endpoints answer 404 and nothing new is ingested; updates already on a profile stay.',
  },
}
